import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { PageTabs } from '@/components';
import { ComplianceTypes } from '@/constants';
import { selectPlayerConfigResult, selectUserCompliance } from '@/store';
import { appStyles } from '@/theme';

import {
  AssessmentReady,
  Bookmarked,
  Completed,
  InProgress,
  QuizReady,
} from './components';
import { selectTabs } from './utils';

export const MyLumiq = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  const compliance = useSelector(selectUserCompliance);
  const { isLoading, isUninitialized } = useSelector(selectPlayerConfigResult);

  const isPlayerConfigLoading = isLoading || isUninitialized;

  const tabs = useMemo(() => {
    return selectTabs({ compliance });
  }, [compliance]);

  const [selectedTab, setSelectedTab] = useState(tabs[0].value);

  useEffect(() => {
    if (initialized || isPlayerConfigLoading) return;

    const urlParamsObject = Object.fromEntries(
      new URLSearchParams(location.search).entries(),
    );
    const parsedTab = urlParamsObject?.tab;

    if (parsedTab) {
      const newSelectedTab =
        tabs.findIndex(tab => tab.value === parsedTab) !== -1
          ? tabs.findIndex(tab => tab.value === parsedTab)
          : 0;
      setSelectedTab(tabs[newSelectedTab].value);
    }

    setInitialized(true);
  }, [location, tabs, initialized, isPlayerConfigLoading]);

  useEffect(() => {
    // Update query params when tab changes
    if (!initialized) return;

    const urlParams = new URLSearchParams(location.search);
    const paramToUpdateIndex = Array.from(urlParams.keys()).find(key =>
      key.startsWith('tab'),
    );
    urlParams.set(paramToUpdateIndex || 'tab', selectedTab);

    navigate(
      {
        pathname: location.pathname,
        search: urlParams.toString(),
      },
      { replace: true },
    );
  }, [navigate, selectedTab, location.pathname, location.search, initialized]);

  return (
    <Box sx={appStyles.limitPageWidth}>
      <Box mb={2.5}>
        <Typography variant="h1">My LumiQ</Typography>
      </Box>
      <Box mb={2.5}>
        <PageTabs
          tabs={tabs}
          selectedTab={selectedTab}
          onChange={(e, newVal) => setSelectedTab(newVal)}
        />
      </Box>

      <Box>
        {selectedTab === tabs[0].value && <InProgress />}
        {compliance === ComplianceTypes.CPD &&
          selectedTab === tabs[1].value && <QuizReady />}
        {compliance === ComplianceTypes.CPE &&
          selectedTab === tabs[1].value && <AssessmentReady />}

        {selectedTab === tabs[2].value && <Bookmarked />}
        {selectedTab === tabs[3].value && <Completed />}
      </Box>
    </Box>
  );
};
